/**
 * **対象に取る（盤に無いカード）の照準**（Issue #597。指示書 `../direction/become_target_elsewhere.md`）。
 * 帯（`effects/become_target_elsewhere_effect.js`）と盤に残る印（`drawObjects.js::targetSight` の `elsewhere`）が
 * **同じ置き場所・同じ塗り方**を使う（帯の終わりと印の始まりで絵が跳ばない）。
 *
 * - 置き場（墓地・除外・デッキ・EX の山 / HUD の手札）から引き出したカード（高さ 500）を、**その山がある側の画面の端**に高さ 300 で残す（v3）
 * - カードの外側に場と同じ**照準の括弧**（`./targetSight.js` の `paintSight`・入れ子・番号の札）、下に**置き場のラベルの札**
 *   （小さな鋼の札・銀の文字＋黒の縁）。プレイヤーの色は括弧と番号の札だけ
 * - 表で見せるのは公開の場所（墓地・表の除外）だけ。手札・デッキ・EX は裏（スリーブ）
 *
 * 座標は**舞台のピクセル**（y 下向き）。three も canvas も import しない（2D の context を受け取って塗る）。
 */
import { CARD_ASPECT, parseZoneKey } from '../../../core/field/zones';
import { rgba } from '../../../canvas/paint';
import { FONT } from './common';
import { FROM, badgeSize, badgeSpot, levelScale, paintSight } from './targetSight';

/** 置き場のラベル */
export const KIND_LABEL = Object.freeze({
  grave: '墓地', removed: '除外', hand: '手札', maindeck: 'デッキ', extra: 'EX',
});
export const labelOf = (zone) => KIND_LABEL[parseZoneKey(zone)?.kind] ?? '';

/**
 * 大きさ（v2。ユーザー 2026-10-01「拡大はもっと大きくていいし、小さくなっているときのサイズが今の大きいときくらいでいい」）:
 * 引き出したときのカードの高さは画面の高さの半分弱、山の横に残る印は v1 の引き出したときの大きさ（300）
 */
export const BIG_H = 500;
export const REST_H = 300;
/** ラベルの文字は残る印（高さ 300）で 46px、カードの高さに比例（46px を下回らない） */
export const LABEL_AT_REST = 46;
/** 山の縁から小さいカードまでの隙間 */
const GAP = 14;
/** HUD（顔の枠・LP・手札）の下端の見当。引き出したカードがここより上に掛からない */
const HUD_BOTTOM = 230;
/** 画面の端に残す印と HUD の手札の並びの間（番号の札が手札の枚数の文字に掛からない） */
const EDGE_GAP = 50;
const MARGIN = 10;

/** カードの高さ → ラベルの文字の大きさ / 番号の札の文字の大きさ */
export const labelFont = (h) => Math.max(LABEL_AT_REST, Math.round((h * LABEL_AT_REST) / REST_H));
export const badgeFont = (h) => Math.max(46, Math.round(h * 0.15));

/** 表で見せるか（墓地・表の除外だけ） */
export function showsFace(zone, card) {
  const kind = parseZoneKey(zone)?.kind;
  if (kind === 'grave') return card?.faceUp !== false;
  if (kind === 'removed') return card?.faceUp !== false;
  return false;
}

const center = (q) => [q.reduce((a, p) => a + p[0], 0) / 4, q.reduce((a, p) => a + p[1], 0) / 4];
const bbox = (q) => {
  const xs = q.map((p) => p[0]); const ys = q.map((p) => p[1]);
  return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
};
const okQuad = (q) => Array.isArray(q) && q.length === 4 && q.every((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
const clamp = (v, a, b) => (b < a ? (a + b) / 2 : Math.max(a, Math.min(b, v)));

/**
 * **置き場の舞台の場所**。
 *
 * 1. 墓地・除外・デッキ・EX は盤のゾーン（一部でも画面に写っていれば。中心は画面の中へ寄せる）。EX を持たない Field（実写）はデッキと魔法・罠の中央を挟んだ向かい
 * 2. 手札・写っていない置き場は HUD の手札（そのプレイヤーの）
 *
 * @param env `{ zoneQuad(key) → 舞台の 4 点, hand(p) → HUD の手札の箱（x, y は中心）, canvas }`
 * @returns `{ cx, cy, w, h, side, via: 'field' | 'hud', rest: 残る小さいカードの高さ }` か null
 */
export function pileSpot(env, zone) {
  const z = parseZoneKey(zone);
  if (!z) return null;
  const canvas = env?.canvas ?? { w: 1920, h: 1080 };
  if (z.kind !== 'hand') {
    let q = env?.zoneQuad?.(zone) ?? null;
    if (!okQuad(q) && z.kind === 'extra') {
      const deck = env?.zoneQuad?.(`maindeck:${z.player}`);
      const mid = env?.zoneQuad?.(`magic_trap:${z.player}:3`);
      if (okQuad(deck) && okQuad(mid)) {
        const [dx, dy] = center(deck); const [mx, my] = center(mid);
        q = deck.map(([x, y]) => [x - dx + 2 * mx - dx, y - dy + 2 * my - dy]);
      }
    }
    if (okQuad(q)) {
      const b = bbox(q);
      // 一部でも画面に写っていればそこ（中心は画面の中へ寄せる。実写の山が画面の上の縁に掛かっていることがある）
      if (b.x < canvas.w && b.x + b.w > 0 && b.y < canvas.h && b.y + b.h > 0) {
        const [qx, qy] = center(q);
        const cx = clamp(qx, 0, canvas.w); const cy = clamp(qy, 0, canvas.h);
        return {
          cx, cy, w: b.w, h: b.h, side: cx < canvas.w / 2 ? -1 : 1, via: 'field',
          rest: REST_H,
        };
      }
    }
  }
  const hand = env?.hand?.(z.player) ?? null;
  if (!hand || !Number.isFinite(hand.x) || !Number.isFinite(hand.y)) return null;
  return {
    cx: hand.x, cy: hand.y, w: hand.w ?? 0, h: hand.h ?? 0, side: z.player === 1 ? 1 : -1, via: 'hud',
    rest: REST_H,
  };
}

/** カード ＋ 括弧 ＋ ラベルの縦の広がり（カードの中心から上 / 下） */
function reach(h) {
  const w = h * CARD_ASPECT;
  const lf = labelFont(h);
  // 括弧の線の太さの余白（`layoutBounds` と同じ）まで含める
  const pad = Math.max(4, w * 0.05) * 2 + 4;
  const up = (h * levelScale(0)) / 2 + badgeFont(h) * 1.3 + pad;
  const down = (h * levelScale(0)) / 2 + lf * 1.32 + 6 + pad;
  return { w, up, down };
}

/**
 * **残る印のカード**の箱 `{ cx, cy, w, h }`（v3。ユーザー 2026-10-01「小さく置いておくところ、もっと端でいい」）。
 * **その山がある側の画面の左右の端**（盤の外の舞台の背景の上。盤・モンスターゾーンに重ねない）。縦は山の高さの見当で、
 * 上の HUD の下端より下・画面の下端より上。同じ置き場の複数は**端に沿って縦に**並べ（括弧が重ならない間隔）、入らなければ詰める。
 *
 * @param slot 同じ置き場の何枚目か / count 同じ置き場の枚数
 */
export function restBox(spot, slot = 0, canvas = { w: 1920, h: 1080 }, count = slot + 1) {
  if (!spot) return null;
  const h = spot.rest ?? REST_H;
  const { w, up, down } = reach(h);
  const half = (w * levelScale(0)) / 2;
  // 括弧の線の太さの余白ぶん内側（描く箱が画面からはみ出さない）
  // 右の端は番号の札（括弧の右上の角から外へ 0.65 ぶん出る。2 桁の幅で見る）も画面に収める
  const overhang = badgeSize(badgeFont(h), 88).w * 0.65 + 4;
  const cx = spot.side < 0 ? half + MARGIN * 2 : canvas.w - half - MARGIN * 2 - overhang;
  // 画面の端は HUD の手札の並び（その下端の見当 + 余白）の真下なので、番号の札が手札の枚数の文字に掛からない高さから
  const lo = HUD_BOTTOM + EDGE_GAP + up; const hi = canvas.h - down - MARGIN;
  const n = Math.max(1, count, slot + 1);
  const i = Math.max(0, Math.min(n - 1, slot));
  // 縦の間隔: 括弧・番号の札・ラベルが重ならない（入らなければ範囲に詰める）
  let step = up + down + GAP;
  const y0 = spot.via === 'hud' ? spot.cy + spot.h / 2 + GAP + up : spot.cy - ((n - 1) * step) / 2;
  if ((n - 1) * step > hi - lo) step = n > 1 ? Math.max(0, hi - lo) / (n - 1) : 0;
  const start = clamp(y0, lo, hi - (n - 1) * step);
  return { cx, cy: clamp(start + i * step, lo, hi), w, h };
}

/** **引き出したときの大きいカード**の箱（山の上へ・盤の外寄りへ。HUD に掛けない・画面に収める） */
export function bigBox(spot, canvas = { w: 1920, h: 1080 }) {
  if (!spot) return null;
  const h = BIG_H;
  const { w, up, down } = reach(h);
  const far = (w * levelScale(0) * FROM) / 2;
  let cx; let cy;
  if (spot.via === 'hud') {
    const outer = spot.cx + spot.side * (spot.w / 2);
    cx = outer - spot.side * (far + MARGIN * 4);
    cy = spot.cy + spot.h / 2 + 40 + up;
  } else {
    cx = spot.cx + spot.side * (spot.w / 2 + w * 0.45);
    cy = spot.cy - h * 0.3;
  }
  return {
    cx: clamp(cx, far + MARGIN, canvas.w - far - MARGIN),
    cy: clamp(cy, HUD_BOTTOM + up, canvas.h - down - MARGIN),
    w,
    h,
  };
}

/** 箱 → 4 点（左上・右上・右下・左下） */
export const rectQuad = (cx, cy, w, h) => [[cx - w / 2, cy - h / 2], [cx + w / 2, cy - h / 2], [cx + w / 2, cy + h / 2], [cx - w / 2, cy + h / 2]];

/**
 * **組み立て**（カードの箱・括弧の 4 点・一番外の括弧・番号の札の場所・ラベルの箱）。
 *
 * @param box カードの箱 `{ cx, cy, w, h }`
 * @param k 括弧の大きさの倍率（締まる動き・脈）
 * @param level 入れ子の段 / depth 一番外の段（`./targetSight.js` と同じ）。label はラベルの文字（札の幅）
 */
export function layout(box, {
  k = 1, level = 0, depth = 0, chain = null, label = '',
} = {}) {
  const { cx, cy, w, h } = box;
  const scale = levelScale(level);
  const quad = rectQuad(cx, cy, w * scale * k, h * scale * k);
  const outer = rectQuad(cx, cy, w * levelScale(depth), h * levelScale(depth));
  const badge = badgeFont(h);
  const spot = badgeSpot(outer, level, depth, badge, chain);
  const lf = labelFont(h);
  const lw = lf * 2.6 + (lf * 0.9 * Math.max(0, String(label || '墓地').length - 2));
  const lh = lf * 1.32;
  const bottom = cy + (h * levelScale(depth)) / 2;
  const tag = { x: cx - lw / 2, y: bottom + 6, w: lw, h: lh, font: lf };
  const width = Math.max(4, w * 0.05);
  return {
    card: { x: cx - w / 2, y: cy - h / 2, w, h }, quad, outer, badge, spot, label: tag, width,
  };
}

/** 組み立ての外接する箱（括弧が一番広がったとき `far` まで含める） */
export function layoutBounds(lay, far = null) {
  const xs = []; const ys = [];
  const push = (x, y) => { xs.push(x); ys.push(y); };
  [...lay.quad, ...lay.outer, ...(far ?? [])].forEach(([x, y]) => push(x, y));
  const bs = badgeSize(lay.badge, 88);
  push(lay.spot[0] - 4, lay.spot[1] - 4); push(lay.spot[0] + bs.w + 4, lay.spot[1] + bs.h + 4);
  push(lay.label.x - 4, lay.label.y - 4); push(lay.label.x + lay.label.w + 4, lay.label.y + lay.label.h + 4);
  const pad = lay.width * 2;
  const x = Math.min(...xs) - pad; const y = Math.min(...ys) - pad;
  return { x, y, w: Math.max(...xs) + pad - x, h: Math.max(...ys) + pad - y };
}

/** 置き場のラベルの札（小さな鋼の札。斜めに切った板・面取りの明るい線・細い金の線・銀の文字＋黒の縁） */
export function paintTag(ctx, { x, y, w, h, font }, text, ox = 0, oy = 0) {
  if (!text) return;
  const X = x - ox; const Y = y - oy;
  const sk = h * 0.32;
  const path = () => {
    ctx.beginPath();
    ctx.moveTo(X + sk, Y); ctx.lineTo(X + w, Y); ctx.lineTo(X + w - sk, Y + h); ctx.lineTo(X, Y + h); ctx.closePath();
  };
  path(); ctx.fillStyle = '#050608'; ctx.lineWidth = Math.max(3, h * 0.16); ctx.strokeStyle = '#050608'; ctx.stroke();
  const g = ctx.createLinearGradient?.(0, Y, 0, Y + h);
  if (g) { g.addColorStop(0, '#3a3f49'); g.addColorStop(0.45, '#1d2027'); g.addColorStop(1, '#101217'); }
  path(); ctx.fillStyle = g ?? '#1d2027'; ctx.fill();
  path(); ctx.strokeStyle = '#c9a24a'; ctx.lineWidth = Math.max(1.5, h * 0.05); ctx.stroke();
  // 面取りの明るい線（上の辺）
  ctx.beginPath(); ctx.moveTo(X + sk + 3, Y + 3); ctx.lineTo(X + w - 3, Y + 3);
  ctx.strokeStyle = rgba('#ffffff', 0.35); ctx.lineWidth = Math.max(1, h * 0.04); ctx.stroke();
  ctx.font = `900 ${font}px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  const tx = X + w / 2; const ty = Y + h / 2 + font * 0.04;
  ctx.lineWidth = Math.max(3, font * 0.2); ctx.strokeStyle = '#050506';
  ctx.strokeText?.(text, tx, ty);
  const tg = ctx.createLinearGradient?.(0, ty - font * 0.55, 0, ty + font * 0.55);
  if (tg) {
    tg.addColorStop(0, '#ffffff'); tg.addColorStop(0.32, '#d9dde6'); tg.addColorStop(0.5, '#8a909c');
    tg.addColorStop(0.52, '#4a4f5a'); tg.addColorStop(0.8, '#9aa1ad'); tg.addColorStop(1, '#e8ebf0');
  }
  ctx.fillStyle = tg ?? '#d9dde6';
  ctx.fillText(text, tx, ty);
}

/** カードを塗る（黒の板の縁 ＋ 絵かスリーブ。届いていなければ地の色） */
export function paintCardFace(ctx, { x, y, w, h }, img, ox = 0, oy = 0, faceUp = true) {
  const X = x - ox; const Y = y - oy;
  const e = Math.max(2, w * 0.045);
  ctx.fillStyle = '#0b0c10';
  ctx.fillRect(X - e, Y - e, w + e * 2, h + e * 2);
  if (img && !(typeof img.complete === 'boolean' && !img.complete)) {
    try { ctx.drawImage(img, X, Y, w, h); } catch (err) { ctx.fillStyle = faceUp ? '#28324e' : '#2a1a4d'; ctx.fillRect(X, Y, w, h); }
  } else {
    ctx.fillStyle = faceUp ? '#28324e' : '#2a1a4d';
    ctx.fillRect(X, Y, w, h);
  }
  ctx.strokeStyle = rgba('#ffffff', 0.7);
  ctx.lineWidth = Math.max(1, w * 0.012);
  ctx.strokeRect(X + 0.5, Y + 0.5, w - 1, h - 1);
}

/**
 * **全部を塗る**（カード → 括弧 → ラベル → 番号の札）。帯と印が同じこれを呼ぶ。
 *
 * @param opts `{ img, faceUp, color, label, chain, sightAlpha, flash, line, badge: 札を出すか, cardAlpha }`
 */
export function paintElsewhere(ctx, lay, ox, oy, {
  img = null, faceUp = true, color = '#ff3355', label = '', chain = null,
  sightAlpha = 1, flash = 0, line = 1, badge = true, cardAlpha = 1,
} = {}) {
  ctx.globalAlpha = cardAlpha;
  paintCardFace(ctx, lay.card, img, ox, oy, faceUp);
  paintTag(ctx, lay.label, label, ox, oy);
  ctx.globalAlpha = cardAlpha * sightAlpha;
  if (sightAlpha > 0) {
    paintSight(ctx, lay.quad, ox, oy, {
      color, width: lay.width * line, flash, chain, badge: badge ? lay.badge : 0, spot: lay.spot,
    });
  }
  ctx.globalAlpha = 1;
}


/**
 * 同じ置き場に残っている「盤に無いカード」の照準の並び（置かれた順・uid ごとに 1 つ）→ `{ slot: その uid の何番目か, count: 枚数 }`。
 * 印はこれで詰め直す（先の印が消えたら寄る）。
 */
export function elsewhereStack(placed, zone, uid) {
  const order = [];
  (placed ?? []).forEach((s) => {
    const d = s?.data ?? {};
    if (s?.mark !== 'targetSight' || !d.elsewhere || d.zone !== zone) return;
    if (!order.includes(d.uid)) order.push(d.uid);
  });
  const i = order.indexOf(uid);
  return { slot: i < 0 ? 0 : i, count: Math.max(1, order.length) };
}
